import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { cn } from '@/lib/utils';

interface Alerta {
  id: number;
  tarefa: string;
  titulo: string;
  detalhe: string | null;
  empresa: number | null;
  link: string | null;
  rotuloLink: string | null;
  desde: string;
  atualizado: string;
}

/**
 * O sino dos alertas das rotinas, no cabeçalho.
 *
 * Cada usuário recebe só o que pode RESOLVER (permissão da área) e da
 * EMPRESA selecionada no cabeçalho. Cada alerta leva à tela onde se resolve,
 * já filtrada. Ele some sozinho quando a rotina deixa de ver o problema;
 * "Ciente" só tira da frente o que ainda não foi resolvido, e o alerta volta
 * se a situação mudar. Sem alerta, o sino nem aparece.
 */
export function SinoAlertas(): JSX.Element | null {
  const { empresa } = useContexto();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const chave = ['alertas', empresa];

  const consulta = useQuery<{ alertas: Alerta[] }>({
    queryKey: chave,
    queryFn: () => api.get<{ alertas: Alerta[] }>('/api/alertas', { empresa: empresa ?? undefined }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const ciente = useMutation({
    mutationFn: (id: number) => api.post(`/api/alertas/${id}/ciente`),
    onMutate: (id) => {
      /* Sai da lista na hora; a resposta só confirma. */
      cliente.setQueryData<{ alertas: Alerta[] }>(chave, (d) =>
        d ? { alertas: d.alertas.filter((a) => a.id !== id) } : d,
      );
    },
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['alertas'] }),
  });

  const alertas = consulta.data?.alertas ?? [];
  if (alertas.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`${alertas.length} alerta(s)`}
          className="relative flex h-10 w-10 items-center justify-center rounded-lg text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
        >
          <Bell className="h-[18px] w-[18px]" />
          <span className="tabular absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-status-atencao px-1 text-[10px] font-bold text-white">
            {alertas.length > 99 ? '99+' : alertas.length}
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 w-[min(420px,calc(100vw-24px))] overflow-hidden rounded-xl border border-borda bg-superficie shadow-forte data-[state=open]:animate-escalar"
        >
          <div className="border-b border-borda px-3.5 py-2.5 text-[12.5px] font-semibold">
            Alertas {empresa ? `· empresa ${empresa}` : ''}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {alertas.map((a) => (
              <div key={a.id} className="border-b border-borda px-3.5 py-3 last:border-b-0">
                <p className="text-[12.5px] font-semibold leading-snug">{a.titulo}</p>
                {a.detalhe ? <p className="mt-0.5 text-[11.5px] text-textoFraco">{a.detalhe}</p> : null}
                <p className="mt-1 text-[10.5px] text-textoSuave">
                  {a.tarefa} · desde{' '}
                  {new Date(a.desde).toLocaleString('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
                <div className="mt-2 flex items-center gap-1.5">
                  {a.link ? (
                    <DropdownMenu.Item
                      onSelect={() => navegar(a.link as string)}
                      className={cn(
                        'cursor-pointer rounded-md bg-primaria-suave px-2.5 py-1 text-[12px] font-semibold text-primaria outline-none',
                        'hover:bg-primaria-suave2 data-[highlighted]:bg-primaria-suave2',
                      )}
                    >
                      {a.rotuloLink ?? 'Abrir'}
                    </DropdownMenu.Item>
                  ) : null}
                  <DropdownMenu.Item
                    onSelect={(e) => {
                      e.preventDefault();
                      ciente.mutate(a.id);
                    }}
                    title="Tira da lista até a situação mudar. Quando o problema é resolvido, o alerta some sozinho."
                    className="flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1 text-[12px] text-textoFraco outline-none hover:bg-superficie2 data-[highlighted]:bg-superficie2"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Ciente
                  </DropdownMenu.Item>
                </div>
              </div>
            ))}
          </div>
          <p className="border-t border-borda px-3.5 py-2 text-[10.5px] text-textoSuave">
            O alerta some sozinho quando o problema for resolvido.
          </p>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
